import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, MapPin, Trophy, Briefcase, Calendar, Clock, 
  Users, Filter, X, ChevronDown, ChevronUp, Star
} from 'lucide-react';
import { toast } from 'sonner';

const INTEREST_LABELS = {
  travel: 'Golf Travel',
  money_games: 'Money Games',
  events: 'Club Events',
  networking: 'Networking'
};

// ─────────────────────────────────────────────────────────────────────────
// Simplified Members section (matches the Facebook-style mockup):
//   • "All Members · N Members" card that scrolls to the full directory
//   • "Club Feed" card with NEW badge → /feed
//   • "Bundle AI Bot" card with description → /chat
// ─────────────────────────────────────────────────────────────────────────
function SimplifiedMembersCards({ totalMembers, onExpandDirectory, onClubFeed, onBundleAI }) {
  return (
    <div className="space-y-3 mb-6" data-testid="simplified-members-cards">
      {/* All Members card */}
      <button
        type="button"
        onClick={onExpandDirectory}
        data-testid="simplified-all-members-card"
        className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border-2 border-bundle-cream-dark hover:border-bundle-green transition-colors shadow-sm text-left"
      >
        <div className="w-12 h-12 rounded-full bg-bundle-green flex items-center justify-center flex-shrink-0">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-base text-bundle-green">All Members</p>
          <p className="text-sm text-gray-500">{totalMembers} Members</p>
        </div>
        <span className="text-bundle-green text-xl">›</span>
      </button>

      {/* Club Feed label */}
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500 pt-1">Club Feed</p>

      {/* Club Feed card */}
      <button
        type="button"
        onClick={onClubFeed}
        data-testid="simplified-club-feed-card"
        className="w-full flex items-center gap-3 p-4 rounded-2xl bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-500 transition-colors shadow-sm text-left"
      >
        <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xl">💬</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-heading font-bold text-base text-bundle-green">Club Feed</p>
            <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-600 px-2 py-0.5 rounded-full">NEW</span>
          </div>
          <p className="text-sm text-gray-600">One place for all posts, updates, and conversations.</p>
        </div>
      </button>

      {/* Bundle AI Bot card */}
      <button
        type="button"
        onClick={onBundleAI}
        data-testid="simplified-bundle-ai-card"
        className="w-full flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-br from-white to-emerald-50/40 border-2 border-emerald-100 hover:border-emerald-500 transition-colors shadow-sm text-left"
      >
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xl">✨</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading font-bold text-base text-bundle-green">Bundle AI Bot</p>
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">AI</span>
          </div>
          <p className="text-sm text-gray-700 leading-snug mt-0.5 italic">"Hi, I'm Bundle AI, your golf community assistant."</p>
          <p className="text-xs text-gray-500 leading-snug mt-1">I share reminders, community posts, recommendations, and answers to your questions.</p>
        </div>
      </button>
    </div>
  );
}

const MemberCard = ({ member, onViewProfile, onTagClick }) => {
  const initials = member.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  
  return (
    <Card 
      data-testid={`member-card-${member.user_id}`}
      className="bg-white border-bundle-cream-dark hover:shadow-lg hover:border-bundle-green/30 transition-all cursor-pointer group"
      onClick={() => onViewProfile(member)}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {member.picture ? (
              <img 
                src={member.picture} 
                alt={member.name} 
                className="w-14 h-14 rounded-full object-cover border-2 border-bundle-cream-dark group-hover:border-bundle-green/40 transition-colors"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-bundle-green flex items-center justify-center text-white font-heading font-bold text-lg border-2 border-bundle-cream-dark group-hover:border-bundle-green/40 transition-colors">
                {initials}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-heading font-bold text-bundle-green text-base truncate group-hover:text-bundle-green-dark transition-colors">
                {member.name}
              </h3>
              {member.role_label === 'Admin' && (
                <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider bg-bundle-gold text-bundle-green-dark px-1.5 py-0.5 rounded-full" title="Golf Club Director">
                  Director
                </span>
              )}
              {member.role_label === 'Fundraiser' && (
                <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded-full" title="Fundraiser">
                  Fundraiser
                </span>
              )}
            </div>
            
            {(member.city || member.state) && (
              <p className="font-body text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {[member.city, member.state].filter(Boolean).join(', ')}
              </p>
            )}

            {member.profession && (
              <p className="font-body text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                <Briefcase className="w-3 h-3 flex-shrink-0" />
                {member.profession}
              </p>
            )}

            {/* Stats Row */}
            <div className="flex items-center gap-3 mt-2.5 flex-wrap">
              {member.handicap != null && (
                <span className="inline-flex items-center gap-1 bg-bundle-green/10 text-bundle-green text-xs font-body font-medium px-2 py-0.5 rounded-full">
                  <Trophy className="w-3 h-3" />
                  {member.handicap} HCP
                </span>
              )}
              {member.home_course && (
                <span className="inline-flex items-center gap-1 bg-bundle-cream text-gray-600 text-xs font-body px-2 py-0.5 rounded-full truncate max-w-[180px]">
                  <Star className="w-3 h-3 flex-shrink-0" />
                  {member.home_course}
                </span>
              )}
              {member.play_frequency && (
                <span className="inline-flex items-center gap-1 bg-bundle-gold/10 text-bundle-gold-dark text-xs font-body px-2 py-0.5 rounded-full">
                  <Calendar className="w-3 h-3" />
                  {member.play_frequency}
                </span>
              )}
            </div>

            {/* Bundle Tags strip — tap any chip to add it to the directory filter */}
            {member.tags && member.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid={`member-card-tags-${member.user_id}`}>
                {member.tags.slice(0, 6).map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onTagClick) onTagClick(t);
                    }}
                    className="inline-flex items-center rounded-full bg-bundle-green/10 text-bundle-green border border-bundle-green/20 hover:bg-bundle-green hover:text-white hover:border-bundle-green px-2 py-0.5 text-[10px] font-bold tracking-wide transition-colors"
                    data-testid={`member-card-tag-${member.user_id}-${t}`}
                  >
                    #{t}
                  </button>
                ))}
                {member.tags.length > 6 && (
                  <span className="text-[10px] font-bold text-gray-400 self-center">+{member.tags.length - 6}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MemberProfileModal = ({ member, onClose }) => {
  if (!member) return null;
  const initials = member.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-bundle-green p-6 rounded-t-2xl relative">
          <button 
            data-testid="close-profile-modal"
            onClick={onClose} 
            className="absolute top-4 right-4 text-white/70 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            {member.picture ? (
              <img src={member.picture} alt={member.name} className="w-20 h-20 rounded-full object-cover border-3 border-white/30" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white font-heading font-bold text-2xl">
                {initials}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading font-bold text-xl text-white">{member.name}</h2>
                {member.role_label === 'Admin' && (
                  <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-bundle-gold text-bundle-green-dark px-2 py-0.5 rounded-full" title="Golf Club Director">
                    Director
                  </span>
                )}
                {member.role_label === 'Fundraiser' && (
                  <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                    Fundraiser
                  </span>
                )}
              </div>
              {(member.city || member.state) && (
                <p className="font-body text-white/80 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {[member.city, member.state].filter(Boolean).join(', ')}
                </p>
              )}
              {member.profession && (
                <p className="font-body text-white/70 text-sm flex items-center gap-1 mt-0.5">
                  <Briefcase className="w-3.5 h-3.5" />
                  {member.profession}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Golf Stats */}
          <div>
            <h3 className="font-heading font-bold text-sm text-bundle-green mb-3 uppercase tracking-wide">Golf Profile</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-bundle-cream rounded-lg p-3 text-center">
                <p className="font-heading font-bold text-lg text-bundle-green">{member.handicap ?? '--'}</p>
                <p className="font-body text-xs text-gray-500">Handicap</p>
              </div>
              <div className="bg-bundle-cream rounded-lg p-3 text-center">
                <p className="font-heading font-bold text-lg text-bundle-green">{member.average_score ?? '--'}</p>
                <p className="font-body text-xs text-gray-500">Avg Score</p>
              </div>
              <div className="bg-bundle-cream rounded-lg p-3 text-center">
                <p className="font-heading font-bold text-lg text-bundle-green">{member.years_playing || '--'}</p>
                <p className="font-body text-xs text-gray-500">Years</p>
              </div>
            </div>
          </div>

          {/* Courses */}
          {(member.home_course || member.favorite_courses) && (
            <div>
              <h3 className="font-heading font-bold text-sm text-bundle-green mb-2 uppercase tracking-wide">Courses</h3>
              {member.home_course && (
                <p className="font-body text-sm text-gray-700">
                  <span className="text-bundle-gold font-medium">Home:</span> {member.home_course}
                </p>
              )}
              {member.favorite_courses && (
                <p className="font-body text-sm text-gray-700 mt-1">
                  <span className="text-bundle-gold font-medium">Favorites:</span> {member.favorite_courses}
                </p>
              )}
            </div>
          )}

          {/* Playing Preferences */}
          {(member.play_frequency || member.preferred_days?.length > 0 || member.preferred_tee_time) && (
            <div>
              <h3 className="font-heading font-bold text-sm text-bundle-green mb-2 uppercase tracking-wide">Playing Preferences</h3>
              <div className="space-y-1.5">
                {member.play_frequency && (
                  <p className="font-body text-sm text-gray-700 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-bundle-gold" />
                    Plays {member.play_frequency}
                  </p>
                )}
                {member.preferred_tee_time && (
                  <p className="font-body text-sm text-gray-700 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-bundle-gold" />
                    Prefers {member.preferred_tee_time} tee times
                  </p>
                )}
                {member.preferred_days?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {member.preferred_days.map(day => (
                      <span key={day} className="bg-bundle-green/10 text-bundle-green text-xs font-body px-2 py-0.5 rounded-full">
                        {day}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Golf Goals */}
          {member.golf_goals && (
            <div>
              <h3 className="font-heading font-bold text-sm text-bundle-green mb-2 uppercase tracking-wide">Golf Goals</h3>
              <p className="font-body text-sm text-gray-700">{member.golf_goals}</p>
            </div>
          )}

          {/* Interests */}
          {member.interested_in?.length > 0 && (
            <div>
              <h3 className="font-heading font-bold text-sm text-bundle-green mb-2 uppercase tracking-wide">Interests</h3>
              <div className="flex flex-wrap gap-2">
                {member.interested_in.map(interest => (
                  <span key={interest} className="bg-bundle-gold/15 text-bundle-gold-dark text-xs font-body font-medium px-3 py-1 rounded-full">
                    {INTEREST_LABELS[interest] || interest}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function MemberDirectory() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  // Paginate so the total member count is never exposed visually
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    state: '',
    profession: '',
    play_frequency: '',
    min_handicap: '',
    max_handicap: ''
  });
  // Bundle Tags filter — AND match
  const [tagFilters, setTagFilters] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate('/');
    if (!loading && user && !user.is_member) {
      toast.error('Active membership required');
      navigate('/membership');
    }
  }, [loading, user, navigate]);

  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (activeFilters.state) params.append('state', activeFilters.state);
      if (activeFilters.profession) params.append('profession', activeFilters.profession);
      if (activeFilters.play_frequency) params.append('play_frequency', activeFilters.play_frequency);
      if (activeFilters.min_handicap) params.append('min_handicap', activeFilters.min_handicap);
      if (activeFilters.max_handicap) params.append('max_handicap', activeFilters.max_handicap);
      if (tagFilters.length > 0) params.append('tags', tagFilters.join(','));
      
      const res = await fetch(`${API}/members/directory?${params.toString()}`, { credentials: 'include' });
      if (res.ok) {
        setMembers(await res.json());
        // Reset to the first page whenever search/filters change
        setVisibleCount(PAGE_SIZE);
      }
    } catch (err) {
      console.error('Failed to load members:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, [searchQuery, activeFilters, tagFilters]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await fetch(`${API}/members/directory/filters`, { credentials: 'include' });
      if (res.ok) setFilters(await res.json());
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  }, []);

  useEffect(() => {
    if (user?.is_member) {
      fetchMembers();
      fetchFilters();
    }
  }, [user, fetchMembers, fetchFilters]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => fetchMembers(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchMembers]);

  // Deep-link: ?openMember=<user_id> auto-opens that member's profile modal
  useEffect(() => {
    const target = searchParams.get('openMember');
    if (!target || members.length === 0) return;
    const m = members.find(x => x.user_id === target);
    if (m) {
      setSelectedMember(m);
      // Clean up the URL so back/refresh doesn't keep re-opening it
      searchParams.delete('openMember');
      setSearchParams(searchParams, { replace: true });
    }
  }, [members, searchParams, setSearchParams]);

  const clearFilters = () => {
    setActiveFilters({ state: '', profession: '', play_frequency: '', min_handicap: '', max_handicap: '' });
    setSearchQuery('');
    setTagFilters([]);
  };

  const hasActiveFilters = Object.values(activeFilters).some(v => v !== '') || searchQuery !== '' || tagFilters.length > 0;

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} />

      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-7 h-7 text-bundle-gold" />
            <h1 data-testid="directory-title" className="font-heading font-bold text-3xl text-bundle-green">Members</h1>
          </div>
          <p className="font-body text-gray-600">Connect with fellow Bundle members</p>
        </div>

        {/* Simplified Members section — mirrors the mockup: 3 big cards */}
        <SimplifiedMembersCards
          totalMembers={members?.length || 0}
          onExpandDirectory={() => {
            document.getElementById('members-directory')?.scrollIntoView({ behavior: 'smooth' });
          }}
          onClubFeed={() => navigate('/feed')}
          onBundleAI={() => navigate('/chat')}
        />

        {/* Search & Filters Bar */}
        <div id="members-directory" className="bg-white rounded-xl border border-bundle-cream-dark p-4 mb-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="directory-search"
                placeholder="Search by name, city, state, or course..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10 border-bundle-cream-dark focus:border-bundle-green"
              />
            </div>
            <Button
              data-testid="toggle-filters-btn"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 border-bundle-cream-dark ${showFilters ? 'bg-bundle-green text-white' : ''}`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Button>
            {hasActiveFilters && (
              <Button
                data-testid="clear-filters-btn"
                variant="ghost"
                onClick={clearFilters}
                className="text-gray-500 hover:text-red-500"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && filters && (
            <div data-testid="filter-panel" className="mt-4 pt-4 border-t border-bundle-cream-dark grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="font-body text-xs text-gray-500 mb-1 block">State</label>
                <select
                  data-testid="filter-state"
                  value={activeFilters.state}
                  onChange={e => setActiveFilters(f => ({ ...f, state: e.target.value }))}
                  className="w-full h-9 px-2 rounded-md border border-bundle-cream-dark text-sm font-body"
                >
                  <option value="">All States</option>
                  {filters.states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="font-body text-xs text-gray-500 mb-1 block">Profession</label>
                <select
                  data-testid="filter-profession"
                  value={activeFilters.profession}
                  onChange={e => setActiveFilters(f => ({ ...f, profession: e.target.value }))}
                  className="w-full h-9 px-2 rounded-md border border-bundle-cream-dark text-sm font-body"
                >
                  <option value="">All</option>
                  {filters.professions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="font-body text-xs text-gray-500 mb-1 block">Play Frequency</label>
                <select
                  data-testid="filter-frequency"
                  value={activeFilters.play_frequency}
                  onChange={e => setActiveFilters(f => ({ ...f, play_frequency: e.target.value }))}
                  className="w-full h-9 px-2 rounded-md border border-bundle-cream-dark text-sm font-body"
                >
                  <option value="">Any</option>
                  {filters.play_frequencies.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="font-body text-xs text-gray-500 mb-1 block">Min Handicap</label>
                <Input
                  data-testid="filter-min-hcp"
                  type="number"
                  step="0.1"
                  placeholder="0"
                  value={activeFilters.min_handicap}
                  onChange={e => setActiveFilters(f => ({ ...f, min_handicap: e.target.value }))}
                  className="h-9 border-bundle-cream-dark text-sm"
                />
              </div>
              <div>
                <label className="font-body text-xs text-gray-500 mb-1 block">Max Handicap</label>
                <Input
                  data-testid="filter-max-hcp"
                  type="number"
                  step="0.1"
                  placeholder="36"
                  value={activeFilters.max_handicap}
                  onChange={e => setActiveFilters(f => ({ ...f, max_handicap: e.target.value }))}
                  className="h-9 border-bundle-cream-dark text-sm"
                />
              </div>
            </div>
          )}

          {/* Bundle Tags filter row — always visible (the killer feature) */}
          <TagFilterBar selected={tagFilters} onChange={setTagFilters} />
        </div>

        {/* Results Info */}
        <div className="flex items-center justify-between mb-4">
          <p data-testid="member-count" className="font-body text-sm text-gray-500">
            {loadingMembers ? 'Loading...' : 'Bundle Golf Club Members'}
          </p>
        </div>

        {/* Members Grid */}
        {loadingMembers ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-gray-500 mb-2">No members found</h3>
            <p className="font-body text-sm text-gray-400">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Be the first to invite your golf buddies!'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="mt-4">
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <div data-testid="members-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {members.slice(0, visibleCount).map(member => (
                <MemberCard
                  key={member.user_id}
                  member={member}
                  onViewProfile={setSelectedMember}
                  onTagClick={(tag) => {
                    setTagFilters((prev) => prev.includes(tag) ? prev : [...prev, tag]);
                    toast.success(`Filtering by #${tag}`);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              ))}
            </div>
            {visibleCount < members.length && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  data-testid="show-more-members-btn"
                  onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                  className="border-bundle-green/30 text-bundle-green hover:bg-bundle-green/5"
                >
                  Show more members
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Profile Modal */}
      {selectedMember && (
        <MemberProfileModal member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TagFilterBar — small one-row filter strip + "Browse all tags" sheet
// Lets members AND-filter the directory by Bundle Tags (the killer feature).
// ──────────────────────────────────────────────────────────────────────
function TagFilterBar({ selected, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    fetch(`${API}/tags/catalog`)
      .then((r) => r.json())
      .then((d) => setCatalog(d.catalog))
      .catch(() => setCatalog({}));
  }, []);

  const toggle = (tag) => {
    if (selected.includes(tag)) onChange(selected.filter((t) => t !== tag));
    else onChange([...selected, tag]);
  };

  // Quick suggestions (curated row): pull a handful from popular categories.
  const quickPicks = catalog ? [
    ...(catalog['Location']?.slice(0, 4) || []),
    ...(catalog['Military & Service']?.slice(6, 8) || []),
    ...(catalog['Golf Interests']?.slice(0, 3) || []),
    ...(catalog['Interests']?.slice(0, 3) || []),
  ] : [];

  return (
    <div className="mt-3 pt-3 border-t border-bundle-cream-dark" data-testid="tag-filter-bar">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] uppercase tracking-widest font-bold text-bundle-green">
          <span className="text-bundle-gold-dark">#</span> Bundle Tags
          {selected.length > 0 && <span className="ml-2 text-gray-400 normal-case tracking-normal">· {selected.length} active</span>}
        </p>
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="text-[11px] font-bold text-bundle-green hover:text-bundle-green-dark"
          data-testid="tag-filter-toggle"
        >
          {expanded ? 'Hide all tags ↑' : 'Browse all tags ↓'}
        </button>
      </div>

      {/* Active filters strip */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              className="inline-flex items-center gap-1 rounded-full bg-bundle-green text-white px-2.5 py-1 text-xs font-bold"
              data-testid={`tag-filter-active-${t}`}
            >
              #{t}
              <span className="text-white/70">×</span>
            </button>
          ))}
        </div>
      )}

      {/* Quick picks row when collapsed */}
      {!expanded && (
        <div className="flex flex-wrap gap-1.5">
          {quickPicks.filter((t) => !selected.includes(t)).slice(0, 12).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              className="inline-flex items-center rounded-full bg-white text-bundle-green border border-bundle-green/30 hover:border-bundle-green hover:bg-bundle-green/5 px-2.5 py-1 text-xs font-bold transition-colors"
              data-testid={`tag-filter-quick-${t}`}
            >
              #{t}
            </button>
          ))}
        </div>
      )}

      {/* Full catalog when expanded */}
      {expanded && catalog && (
        <div className="space-y-3 mt-2 max-h-72 overflow-y-auto pr-1">
          {Object.entries(catalog).map(([cat, tags]) => (
            <div key={cat}>
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">{cat}</p>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => {
                  const active = selected.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggle(t)}
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${
                        active ? 'bg-bundle-green text-white' : 'bg-white text-bundle-green border border-bundle-green/30 hover:bg-bundle-green/5'
                      }`}
                    >
                      #{t}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

